import { NavLink } from "react-router-dom";
import styled from "styled-components";

import {
  DATA_E2E_KEY,
  IStyledAttributes,
  MOBILE_BREAKPOINT_NUMBER,
} from "@ryde/shared";
import {
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  orangeMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

export const HelpTopicsPageStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "help-topics-page",
})`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${greyMui._50};
  position: relative;
`;

export const HelpTopicsSidebarBackdropStyled = styled.button.attrs({
  type: "button",
  "aria-label": "Close topics menu",
})<{ $visible: boolean }>`
  display: none;
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: ${({ $visible }) => ($visible ? "block" : "none")};
    position: fixed;
    inset: 0;
    z-index: ${ScreenDepth.MENU_DRAWER_BACKDROP};
    background: rgb(15 23 42 / 0.38);
  }
`;

export const HelpTopicsSidebarStyled = styled.aside.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "help-topics-sidebar",
})<{ $isMobileOpen?: boolean }>`
  width: 300px;
  min-width: 260px;
  flex-shrink: 0;
  border-inline-end: 1px solid ${greyMui._200};
  background: ${rydeColors.white};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 2px 0 12px rgb(15 23 42 / 0.04);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    position: fixed;
    top: 0;
    bottom: 0;
    inset-inline-start: 0;
    z-index: ${ScreenDepth.LEFT_BAR};
    width: min(320px, 88vw);
    min-width: unset;
    transform: translateX(
      ${({ $isMobileOpen }) => ($isMobileOpen ? "0" : "-100%")}
    );
    transition: transform 0.28s ease;
    box-shadow: 4px 0 24px rgb(15 23 42 / 0.14);
  }
`;

export const HelpTopicsSidebarHeaderStyled = styled.div`
  padding: 20px 16px 14px;
  border-bottom: 1px solid ${greyMui._200};

  h1 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: ${greyMui._900};
  }

  p {
    margin: 6px 0 0;
    font-size: 0.8125rem;
    color: ${greyMui._500};
    line-height: 1.45;
  }
`;

export const HelpTopicsSearchFieldWrapper = styled.div`
  margin-top: 14px;

  .MuiOutlinedInput-root {
    background: ${greyMui._50};
    border-radius: 10px;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${greyMui._200};
  }

  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${blueMui._200};
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${blueMui._400};
  }
`;

export const HelpTopicsSidebarScrollStyled = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 12px 10px 18px;
`;

export const HelpTopicsSidebarEmptyStyled = styled.p`
  margin: 12px 8px 0;
  font-size: 0.8125rem;
  color: ${greyMui._500};
  line-height: 1.45;
`;

export const HelpTopicsSectionBlockStyled = styled.div`
  margin-bottom: 10px;
`;

export const HelpTopicsGroupTitleStyled = styled.h2`
  margin: 0 0 8px;
  padding: 0 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${greyMui._500};
`;

export const HelpTopicsSectionTitleStyled = styled.h3`
  margin: 14px 0 6px;
  padding: 0 8px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${orangeMui._700};
`;

export const HelpTopicsNavLinkStyled = styled(NavLink)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  margin-bottom: 2px;
  border-radius: 8px;
  text-decoration: none;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;

  .HelpTopicsNavLink-primary {
    color: ${greyMui._800};
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.35;
  }

  .HelpTopicsNavLink-secondary {
    color: ${greyMui._500};
    font-size: 0.6875rem;
    line-height: 1.25;
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
      "Courier New", monospace;
  }

  &:hover {
    background: ${greyMui._100};

    .HelpTopicsNavLink-primary {
      color: ${greyMui._900};
    }
  }

  &.active {
    background: ${blueMui._50};
    box-shadow: inset 3px 0 0 ${blueMui._500};

    .HelpTopicsNavLink-primary {
      color: ${blueMui._800};
    }

    .HelpTopicsNavLink-secondary {
      color: ${blueMui._600};
    }
  }
`;

export const HelpTopicsContentStyled = styled.main.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "help-topics-content",
})`
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px clamp(32px, 5vw, 88px) 40px clamp(40px, 5vw, 72px);

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    align-items: stretch;
    padding: 0 0 20px;
  }
`;

export const HelpTopicsContentInnerStyled = styled.div`
  width: 100%;
  max-width: min(980px, 100%);

  @media (min-width: ${MOBILE_BREAKPOINT_NUMBER + 1}px) {
    margin-inline: auto;
  }
`;

export const HelpTopicsMobileToolbarStyled = styled.div`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    align-items: center;
    gap: 8px;
    position: sticky;
    top: 0;
    z-index: ${ScreenDepth.HEADER_FORM_CONTROLS};
    padding: 10px 12px;
    background: ${rydeColors.white};
    border-bottom: 1px solid ${greyMui._200};
    box-shadow: 0 2px 8px rgb(15 23 42 / 0.05);
  }

  .HelpTopicsMobileToolbar-title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${greyMui._900};
  }
`;

export const HelpTopicsWelcomeStyled = styled.div`
  max-width: 720px;

  h2 {
    margin: 0 0 10px;
    font-size: 1.5rem;
    color: ${greyMui._900};
  }

  p {
    margin: 0 0 14px;
    color: ${greyMui._600};
    line-height: 1.6;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 20px 16px 0;
  }
`;

export const HelpTopicsTopicToolbarStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "help-topics-topic-toolbar",
  },
)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  min-height: 40px;
  position: sticky;
  top: 0;
  z-index: 2;
  padding-bottom: 8px;
  background: linear-gradient(
    180deg,
    ${rydeColors.white} 72%,
    rgb(255 255 255 / 0)
  );
`;

export const HelpTopicsArticleStyled = styled.article`
  position: relative;
  max-width: 100%;
  background: ${rydeColors.white};
  border: 1px solid ${greyMui._200};
  border-radius: 14px;
  padding: 16px 30px 34px;
  box-shadow: 0 8px 24px rgb(15 23 42 / 0.05);
  overflow-x: auto;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    border-radius: 0;
    border-inline: none;
    padding: 18px 16px 24px;
    box-shadow: none;
  }
`;
